/* =============================================================================
   Historic Issuing Authorities — content file (build pack §7).

   One record per letter A to Z. Authority metadata and captions are the
   approved v13 prototype editorial (verified: B = Lancashire, O = Birmingham);
   every image credit and source URL is REAL, resolved from the Historic
   Authority Image Researcher archive (rights-checked candidates only).
   Photographs: the 16 letters whose images are Creative Commons or public
   domain are HOSTED by PlateX (assets/dateless-authorities/, downloaded at
   production resolution from the cited source) and render as real images
   with their photographer and licence shown. The two auction-sourced letters
   (B Historics, K Bonhams) stay on the archive frame until their permission
   is confirmed, since credit-and-link does not by itself license hosting.

   Card states: ready (frame + caption + credit) · gap (line-art + contribute)
   · never (never-issued explanation). Never fabricate an archive reference.
   ========================================================================== */

export type AuthorityCardState = 'ready' | 'gap' | 'never';

/** Readiness ladder (approval ruling): an authority is FULLY ready only when
    the production image asset exists. Until then a ready-state card renders
    the archive frame, never a photograph.
      metadata-approved  editorial verified, no rights decision yet
      rights-approved    licence/permission settled; production upload pending
      image-uploaded     production asset live (imageAsset set)            */
export type AuthorityReadiness =
  | 'metadata-approved'
  | 'rights-approved'
  | 'image-uploaded';

export interface AuthorityRecord {
  readiness?: AuthorityReadiness;   // ready-state records only
  letter: string;
  authorityName: string;        // "Never issued" for I / Q
  region: string;               // England | Scotland | Wales | Ireland | '-'
  populationRank?: string;      // "1st by population" (E&W allocation only)
  issueEnd?: string;            // year the authority exhausted combinations
  reversedFrom1953?: boolean;   // issued numbers-first marks 1953 onward
  notableMark?: string;         // e.g. "A 1", "1000 E"
  note?: string;                // sourced historical note (editorial)
  state: AuthorityCardState;
  registration?: string;        // the pictured mark
  caption?: string;
  credit?: string;
  licence?: string;             // e.g. "CC BY-SA 2.0"; empty => sale credit
  sourceUrl?: string;           // every image card links to its source
  imageAsset?: string | null;   // S3 key once the rights pipeline lands
  /** Cropped to the vehicle and its plate so that no identifiable person is
      shown. CC BY and CC BY-SA both require a change to be indicated, so this
      also drives the "cropped" note in the credit line. */
  cropped?: boolean;
  gapNote?: string;             // why no image yet (gap state)
}

export const AUTHORITIES: AuthorityRecord[] = [
  {
    letter: 'A', authorityName: 'London', region: 'England', populationRank: '1st by population',
    issueEnd: '1905', notableMark: 'A 1',
    note: 'Allocated first, by 1901 population, and with the most cars in Britain, London used every A-combination within two years. The first authority to run out. A 1 went to Earl Russell of the LCC.',
    state: 'ready', readiness: 'image-uploaded', registration: 'A 883',
    caption: 'MMC electric brougham carrying A 883, at the London to Brighton Veteran Car Run, 2017.',
    credit: 'Peter Trimming / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AMMC%2C_A_883%2C_London_to_Brighton_Veteran_Car_Run_2017_-_geograph.org.uk_-_5594459.jpg',
    imageAsset: 'assets/dateless-authorities/A_A883.jpg', cropped: true,
  },
  {
    letter: 'B', authorityName: 'Lancashire', region: 'England', populationRank: '2nd by population',
    issueEnd: '1919',
    state: 'ready', readiness: 'rights-approved', registration: 'B 200',
    caption: '1906 Wolseley Siddeley 15HP carrying B 200.',
    credit: 'Historics Auctioneers, Windsorview Lakes Sale, 22 July 2023', licence: '',
    sourceUrl: 'https://www.historics.co.uk/auction/lot/lot-136---1906-wolseley-siddeley-15hp/?lot=3745&sd=1',
    imageAsset: null,
  },
  {
    letter: 'C', authorityName: 'West Riding of Yorkshire', region: 'England', populationRank: '3rd by population',
    issueEnd: '1912',
    state: 'ready', readiness: 'image-uploaded', registration: 'C 597',
    caption: '1904 Humber carrying C 597, Brighton Run 2014.',
    credit: 'Robert Hummerstone', licence: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AHumber%2C_C_597%2C_London_to_Brighton_Veteran_Car_Run_2014%2C_Humberette_1904.jpg',
    imageAsset: 'assets/dateless-authorities/C_C597.jpg', cropped: true,
  },
  {
    letter: 'D', authorityName: 'Kent', region: 'England', issueEnd: '1913', reversedFrom1953: true,
    state: 'ready', readiness: 'image-uploaded', registration: 'D 165',
    caption: '1903 Maxim carrying D 165, Regent Street Motor Show 2015.',
    credit: 'Tim Frost from Braintree, England', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AMaxim%2C_D_165%2C_Regent_Street_Motor_Show_2015%2C_1903_16_HP_Rear-Entrance_Tonneau_24014396505.jpg',
    imageAsset: 'assets/dateless-authorities/D_D165.jpg', cropped: true,
  },
  {
    letter: 'E', authorityName: 'Staffordshire', region: 'England', issueEnd: '1925', reversedFrom1953: true,
    notableMark: '1000 E',
    note: 'Pioneered the reversed format with 1000 E in 1953. Also carried by 2 E.',
    state: 'gap', gapNote: 'Our photographs of E 449 do not show the plate legibly.',
  },
  {
    letter: 'F', authorityName: 'Essex', region: 'England', issueEnd: '1915', reversedFrom1953: true,
    notableMark: '1 F',
    note: 'Carried by 1 F, sold for £926,000 with fees at Sotheby’s in 2025, the most expensive British registration ever sold publicly.',
    state: 'ready', readiness: 'image-uploaded', registration: 'F 28',
    caption: '1898 Panhard et Levassor carrying F 28, Brighton Run.',
    credit: 'Peter Trimming / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APanhard_%26_Levassor%2C_F_28%2C_London_to_Brighton_Veteran_Car_Run_2019%2C_6_CV_1898_-_geograph.org.uk_-_6317279.jpg',
    imageAsset: 'assets/dateless-authorities/F_F28.jpg', cropped: true,
  },
  {
    letter: 'G', authorityName: 'Glasgow', region: 'Scotland', issueEnd: '1921', notableMark: 'G 1',
    note: 'G 1 broke £500,000 in 2011, the first registration to do so.',
    state: 'gap', gapNote: 'No image located yet.',
  },
  {
    letter: 'H', authorityName: 'Middlesex', region: 'England', issueEnd: '1912', reversedFrom1953: true,
    note: 'Exhausted early, and in 1963 issued the first suffix mark, AHX 1A, ending dateless issue.',
    state: 'ready', readiness: 'image-uploaded', registration: 'H 786',
    caption: '1899 Panhard et Levassor carrying H 786, plate clearly legible.',
    credit: 'Creator unknown, via Wikimedia Commons', licence: 'CC0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APanhard_%26_Levassor%2C_H_786%2C_London_to_Brighton_Veteran_Car_Run_2010%2C_6_CV_1899_%285155441421%29.jpg',
    imageAsset: 'assets/dateless-authorities/H_H786.jpg', cropped: true,
  },
  {
    letter: 'I', authorityName: 'Never issued', region: '-', state: 'never',
    note: '‘I’ was never used as a mainland area code, kept for Ireland.',
  },
  {
    letter: 'J', authorityName: 'County Durham', region: 'England', issueEnd: '1922',
    state: 'ready', readiness: 'image-uploaded', registration: 'J 5',
    caption: 'Pope-Hartford carrying J 5, Brighton Run.',
    credit: 'Andrew Skudder from Crawley, UK', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APope-Hartford%2C_J_5%2C_London_to_Brighton_Veteran_Car_Run_2008%2C_1904_%282995039861%29.jpg',
    imageAsset: 'assets/dateless-authorities/J_J5.jpg', cropped: true,
  },
  {
    letter: 'K', authorityName: 'Liverpool', region: 'England', issueEnd: '1914', reversedFrom1953: true,
    state: 'ready', readiness: 'rights-approved', registration: 'K 528',
    caption: '1909 Wolseley-Siddeley 14hp carrying K 528.',
    credit: 'Bonhams, Collectors’ Motor Cars and Automobilia, 30 April 2007', licence: '',
    sourceUrl: 'https://cars.bonhams.com/auction/15339/lot/718/1909-wolseley-siddeley-14hp-four-seat-tourer-chassis-no-6755-engine-no-253214/',
    imageAsset: null,
  },
  {
    letter: 'L', authorityName: 'Glamorgan', region: 'Wales', issueEnd: '1921',
    state: 'ready', readiness: 'image-uploaded', registration: 'L 39',
    caption: 'L 39 at the Brighton Run start.',
    credit: 'Jon Lavis', licence: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APanhard_%26_Levassor%2C_L_39%2C_London_to_Brighton_Veteran_Car_Run_2024%2C_Type_A2_7_CV_1902_-_59.jpg',
    imageAsset: 'assets/dateless-authorities/L_L39.jpg', cropped: true,
  },
  {
    letter: 'M', authorityName: 'Cheshire', region: 'England', issueEnd: '1919',
    state: 'ready', readiness: 'image-uploaded', registration: 'M 18',
    caption: '1903 Panhard et Levassor carrying M 18, Regent Street Motor Show.',
    credit: 'Ank Kumar', licence: 'CC BY-SA 4.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APanhard_%26_Levassor%2C_M_18%2C_Regent_Street_Motor_Show_2012%2C_15_CV_1903_%28Ank_Kumar%29_02.jpg',
    imageAsset: 'assets/dateless-authorities/M_M18.jpg', cropped: true,
  },
  {
    letter: 'N', authorityName: 'Manchester', region: 'England', issueEnd: '1913', reversedFrom1953: true,
    state: 'gap',
    gapNote: 'The strongest candidate, a period street photograph of N 1, is not yet cleared for use; no Commons fallback.',
  },
  {
    letter: 'O', authorityName: 'Birmingham', region: 'England', issueEnd: '1913', notableMark: '25 O',
    note: 'Carried by 25 O, sold for £513,680 on the PlateX verified-corpus basis in 2014, the most expensive registration the DVLA has ever sold.',
    state: 'ready', readiness: 'image-uploaded', registration: 'O 53',
    caption: 'Wolseley carrying O 53, 1985 Brighton Run. Fallback image; the period options (O 53 at Donnington Hurst 1943, O 661 on the Snowdon Mountain Railway) are pending rights clearance.',
    credit: 'Peter Shimmon / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AWolseley%2C_O_53%2C_London_to_Brighton_Veteran_Car_Run_1985_%281%29_-_geograph.org.uk_-_3209511.jpg',
    imageAsset: 'assets/dateless-authorities/O_O53.jpg',
  },
  {
    letter: 'P', authorityName: 'Surrey', region: 'England', issueEnd: '1919',
    state: 'ready', readiness: 'image-uploaded', registration: 'P 26',
    caption: 'Dennis carrying P 26, Brighton Run.',
    credit: 'Peter Trimming / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3ADennis%2C_P_26%2C_London_to_Brighton_Veteran_Car_Run_2017_-_geograph.org.uk_-_5594624.jpg',
    imageAsset: 'assets/dateless-authorities/P_P26.jpg', cropped: true,
  },
  {
    letter: 'Q', authorityName: 'Never issued', region: '-', state: 'never',
    note: 'Reserved; ‘Q’ now marks vehicles of unknown age.',
  },
  {
    letter: 'R', authorityName: 'Derbyshire', region: 'England', issueEnd: '1923', reversedFrom1953: true,
    state: 'ready', readiness: 'image-uploaded', registration: 'R 23',
    caption: 'Wolseley carrying R 23, plate clearly legible head-on.',
    credit: 'Les Chatfield', licence: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AWolseley%2C_R_23%2C_London_to_Brighton_Veteran_Car_Run_2010%2C_built_1903%2C_5155252185.jpg',
    imageAsset: 'assets/dateless-authorities/R_R23.jpg',
  },
  {
    letter: 'S', authorityName: 'Edinburgh', region: 'Scotland', issueEnd: '1920',
    state: 'gap', gapNote: 'The only candidate is doubtful; the plate reads as S 88 C.',
  },
  {
    letter: 'T', authorityName: 'Devon', region: 'England', issueEnd: '1920',
    state: 'gap', gapNote: 'Our photograph of T 60 does not show the plate legibly.',
  },
  {
    letter: 'U', authorityName: 'Leeds', region: 'England', issueEnd: '1921', reversedFrom1953: true,
    state: 'ready', readiness: 'image-uploaded', registration: 'U 508',
    caption: '1903 Panhard et Levassor carrying U 508, plate clearly legible.',
    credit: 'Les Chatfield from Brighton, England', licence: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3APanhard_%26_Levassor%2C_U_508%2C_London_to_Brighton_Veteran_Car_Run_2010%2C_flickr_Panhard_et_Levassor_1903.JPG',
    imageAsset: 'assets/dateless-authorities/U_U508.jpg',
  },
  {
    letter: 'V', authorityName: 'Lanarkshire', region: 'Scotland', issueEnd: '1922',
    state: 'ready', readiness: 'image-uploaded', registration: 'V 46',
    caption: '1927 press photograph of a Clement carrying V 46, Brighton Run.',
    credit: 'Le Miroir des sports, 22 novembre 1927', licence: 'Public domain',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3AClement%2C_V_46%2C_London_to_Brighton_Veteran_Car_Run_1927%2C_Type_VCP_called_Clement-Panhard%2C_Le_premier_%27London-to-Brighton_veteran_car_run%27_en_novembre_1927_%28pour_v%C3%A9hicules_de_plus_de_trente_ans%29.jpg',
    imageAsset: 'assets/dateless-authorities/V_V46.jpg',
  },
  {
    letter: 'W', authorityName: 'Sheffield', region: 'England', issueEnd: '1919', reversedFrom1953: true,
    state: 'ready', readiness: 'image-uploaded', registration: 'W 389',
    caption: 'Darracq carrying W 389, Regent Street Motor Show.',
    credit: 'Robert Lamb / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3ADarracq%2C_W_389%2C_Regent_Street_Motor_Show_2015%2C_12_CV_1903_rear-entrance_tonneau_-_geograph.org.uk_-_4723089.jpg',
    imageAsset: 'assets/dateless-authorities/W_W389.jpg', cropped: true,
  },
  {
    letter: 'X', authorityName: 'Northumberland', region: 'England', issueEnd: '1921',
    state: 'ready', readiness: 'image-uploaded', registration: 'X 605',
    caption: '1902 Lambert carrying X 605.',
    credit: 'Peter Trimming from Croydon, England', licence: 'CC BY 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3ALambert%2C_X_605%2C_London_to_Brighton_Veteran_Car_Run_2011%2C_1902_-_6322045946.jpg',
    imageAsset: 'assets/dateless-authorities/X_X605.jpg', cropped: true,
  },
  {
    letter: 'Y', authorityName: 'Somerset', region: 'England', issueEnd: '1921',
    note: 'The last English/Welsh letter allocated by population.',
    state: 'ready', readiness: 'image-uploaded', registration: 'Y 66',
    caption: 'De Dion-Bouton carrying Y 66, Brighton Run.',
    credit: 'Peter Trimming / Geograph Britain and Ireland', licence: 'CC BY-SA 2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File%3ADe_Dion-Bouton%2C_Y_66%2C_London_to_Brighton_Veteran_Car_Run_2025_-_geograph.org.uk_-_8188418.jpg',
    imageAsset: 'assets/dateless-authorities/Y_Y66.jpg', cropped: true,
  },
  {
    letter: 'Z', authorityName: 'Dublin', region: 'Ireland',
    note: 'An Irish allocation issued in Ireland under the same 1903 scheme, rather than on mainland Britain.',
    state: 'gap', gapNote: 'No image located yet; Dublin issue, Irish archives are the likely source.',
  },
];
